import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { ClaimService } from 'src/app/services/claim.service';
import { CustomerService } from 'src/app/services/customer.service';
import { skip, Subscription } from 'rxjs';
import {
  ClaimDetailItem,
  applyClaimDetailVo,
  getClaimStatusColor,
  getClaimStatusLabel,
} from './claim-detail-view.util';

@Component({
  selector: 'claim-detail',
  templateUrl: './claim-detail.component.html',
  styleUrls: ['./claim-detail.component.less'],
})
export class ClaimDetailComponent implements OnInit, OnDestroy {
  claimId = '';
  claimNumber = '';
  customerReferenceId = '';
  claimDescription = '';

  private customerChangeSub?: Subscription;
  claimStatus = '';
  claimStatusName = '';
  referenceTypeReady = false;

  basicForm!: FormGroup;
  otherForm!: FormGroup;
  detailLoading = false;
  itemsLoading = false;
  customerDocTypeLoading = false;
  claimReferences: any[] = [];

  // Keep value mapping aligned with create-claim page.
  referenceTypeOptions = [
    { labelKey: 'CLAIM_STANDARD_BINDING_ORDER', value: 'normal' },
    { labelKey: 'CLAIM_CONSIGNMENT_ORDER', value: 'consignment' },
  ];

  get selectedReferenceTypeOptions(): { labelKey: string; value: string }[] {
    if (!this.referenceTypeReady) {
      return [];
    }
    const selected = this.basicForm?.get('referenceType')?.value;
    if (!selected) {
      return [];
    }
    return this.referenceTypeOptions.filter(opt => opt.value === selected);
  }

  reasonOptions: { label: string; value: string }[] = [];
  categoryOptions: { label: string; value: string }[] = [];
  indexOptions: { label: string; value: string }[] = [];
  schemeOptions: { label: string; value: string }[] = [];

  selfReturnOptions = [
    { labelKey: 'CLAIM_CREATE_RETURN_BY_SELF', value: 'yes' },
    { labelKey: 'CLAIM_CREATE_BOSCH_PICKUP', value: 'no' },
  ];

  warehouseInfo = {
    address: '江苏省苏州市太仓市港口开发区金港路2号博世亚太物流中心A1仓库',
    contact: '侯昊松',
    phone: '13526207938',
  };

  detailItems: ClaimDetailItem[] = [];

  // Display fields for basic info (read-only)
  claimTypeName = '';
  claimCategoryName = '';
  ruleLevel2Name = '';
  contactAddress = '';
  contactName = '';
  contactPhone = '';
  returnSo = '';
  returnDn = '';
  trackingNum = '';
  trackingNumSaving = false;

  detailPagination = {
    pageIndex: 1,
    pageSize: 5,
  };

  get pagedDetailItems(): ClaimDetailItem[] {
    const start = (this.detailPagination.pageIndex - 1) * this.detailPagination.pageSize;
    return this.detailItems.slice(start, start + this.detailPagination.pageSize);
  }

  // ---- Status chip helpers (aligned with create-claim) ----

  getStatusLabel(status: string): string {
    return getClaimStatusLabel(status);
  }

  getStatusColor(status: string): { color: string; bg: string } {
    return getClaimStatusColor(status);
  }

  detailPageIndexChange(pageIndex: number): void {
    this.detailPagination.pageIndex = pageIndex;
  }

  attachments: { attachmentId: string; name: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
    private claimService: ClaimService,
    private customerService: CustomerService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.basicForm = this.fb.group({
      referenceType: ['normal'],
      reason: [null],
      category: [null],
      indexNumber: [null],
      scheme: [null],
      selfReturn: ['no'],
      customerAddress: [''],
      customerContact: [''],
      customerPhone: [''],
    });

    this.otherForm = this.fb.group({
      remark: [''],
    });

    this.claimId =
      this.route.snapshot.paramMap.get('id') ||
      this.route.snapshot.paramMap.get('claimNumber') ||
      '';

    this.claimNumber =
      (this.router.getCurrentNavigation()?.extras?.state as any)?.claimNumber ||
      '';

    if (this.claimId) {
      this.fetchClaimDetail(this.claimId);
    }

    // 切换客户时跳转回索赔概览页
    this.customerChangeSub = this.customerService.selectedCustomer$
      .pipe(skip(1))
      .subscribe(() => {
        this.handleCancel();
      });
  }

  ngOnDestroy(): void {
    this.customerChangeSub?.unsubscribe();
  }

  /** 非寄售 + 索赔原因=价格差异 + 索赔分类=价格差异 时显示「正确的销售单价」列 */
  get isPriceDifferenceMode(): boolean {
    if (this.basicForm?.get('referenceType')?.value !== 'normal') return false;
    return this.claimTypeName === '价格差异' && this.claimCategoryName === '价格差异';
  }

  /**
   * 索赔方式为 Return for Credit / Exchange / Return 时才展示
   * 「是否自行寄回」区块及其关联的客户取件信息。
   */
  get showSelfReturnSection(): boolean {
    const schemeValue = this.basicForm?.get('scheme')?.value;
    if (!schemeValue) {
      return false;
    }
    const allowed = ['Return for Credit', 'Exchange', 'Return'];
    if (allowed.includes(this.ruleLevel2Name?.trim())) {
      return true;
    }
    const matched = this.schemeOptions.find(opt => opt.value === schemeValue);
    const label = (matched?.label ?? '').toString().trim();
    return allowed.includes(label) || allowed.includes(schemeValue.toString().trim());
  }

  get showTrackingNumField(): boolean {
    return this.showSelfReturnSection && this.basicForm?.get('selfReturn')?.value === 'yes';
  }

  get warehouseInfoText(): string {
    return `仓库地址：${this.warehouseInfo.address}； 仓库联系人：${this.warehouseInfo.contact}； 仓库电话：${this.warehouseInfo.phone}`;
  }

  get isOrderMode(): boolean {
    // 需求：寄售(consignment) 时为订单模式。
    return this.basicForm?.get('referenceType')?.value === 'consignment';
  }

  private fetchClaimDetail(id: string): void {
    this.referenceTypeReady = false;
    this.detailLoading = true;
    this.claimService.apiClaimDetail(id).subscribe({
      next: (result: any) => {
        this.detailLoading = false;
        const vo = result?.rtnData;
        if (!vo) {
          return;
        }
        this.applyClaimDetail(vo);
        this.referenceTypeReady = true;
      },
      error: () => {
        this.detailLoading = false;
        this.message.error(this.translate.instant('CLAIM_MSG_DETAIL_FAIL'));
      },
    });
  }

  private applyClaimDetail(vo: any): void {
    applyClaimDetailVo(this, vo);
    this.customerReferenceId = vo.customerReferenceId ?? '';
    this.claimDescription = `${vo.description ?? ''}`.trim();
    this.basicForm.disable();
    this.otherForm.disable();
  }

  saveTrackingNum(): void {
    if (!this.claimId) {
      return;
    }
    const trimmed = (this.trackingNum ?? '').trim();
    if (!trimmed) {
      this.message.warning(this.translate.instant('CLAIM_DETAIL_TRACKING_REQUIRED'));
      return;
    }
    this.trackingNumSaving = true;
    this.claimService
      .apiClaimUpdateTrackingNum({
        id: Number(this.claimId) || this.claimId,
        trackingNum: trimmed,
      })
      .subscribe({
        next: (result: any) => {
          this.trackingNumSaving = false;
          if (result?.rtnCode === 10000) {
            this.trackingNum = trimmed;
            this.message.success(this.translate.instant('CLAIM_DETAIL_TRACKING_SAVED'));
            this.navigateBackToOverview();
          } else {
            this.message.error(
              result?.rtnMsg ?? this.translate.instant('CLAIM_MSG_SAVE_FAIL'),
            );
          }
        },
        error: (err: any) => {
          this.trackingNumSaving = false;
          this.message.error(
            err?.error?.rtnMsg ?? this.translate.instant('CLAIM_MSG_SAVE_FAIL'),
          );
        },
      });
  }

  private navigateBackToOverview(): void {
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    this.router.navigate([country, 'claim']);
  }

  handleCancel(): void {
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    this.router.navigate([country, 'claim']);
  }

  downloadAttachment(att: { attachmentId: string; name: string }): void {
    this.claimService
      .apiClaimAttachmentDownload(att.attachmentId, att.name)
      .subscribe({
        next: response => {
          const blob = response.body;
          if (!blob) {
            this.message.error(this.translate.instant('CLAIM_DETAIL_DOWNLOAD_EMPTY'));
            return;
          }
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = att.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        error: () =>
          this.message.error(this.translate.instant('CLAIM_DETAIL_DOWNLOAD_FAIL')),
      });
  }
}
